import { AppLinkNavigationModel } from "client/Api/Apps/AppLinks/appLink";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import { ContentBlock } from "client/Features/PublicPage/ContentBlock";
import {
	LinkKind,
	useLinkToIntegrationCredentialsDialog
} from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import { ensureNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo, reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { ReactElement, useEffect } from "react";
import { createRoot } from "react-dom/client";

type GoPageProps = {
	model: AppLinkNavigationModel;
};

const GoPage = (props: GoPageProps): ReactElement => {
	const {
		model
	} = props;

	const [integrationCredentialsDialog, openIntegrationCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	useEffect(
		(): void => {
			(async (): Promise<void> => {
				if (model.requireIntegrationCredentialsToBeProvidedFirst) {
					const result: TryResult<void> = await openIntegrationCredentialsDialog({
						thingToLink: {
							kind: LinkKind.EnvironmentApp,
							id: model.environmentAppId
						},
						disableDialogCancellation: true
					});

					if (!result.wasSuccessful) {
						throw new Error("Cancelling the dialog should not be possible");
					}

					reloadCurrentPage();
				} else if (!model.hasNoAccessToTheExternalApplication) {
					await offerPasswordUpdateWhenPossibleChanged({
						kind: IntegrationCredentialsLinkKind.EnvironmentApp,
						appId: model.environmentAppId
					});

					navigateTo(model.url);
				}
			})();
		},
		[
			model,
			openIntegrationCredentialsDialog
		]
	);


	let titleKey: LocalizationKey = "_NavigatingToYourApp";
	let descriptionKey: LocalizationKey | undefined;

	if (model.hasNoAccessToTheExternalApplication) {
		titleKey = "_ThisAppIsNotAvailableToYou";
		descriptionKey = "_PleaseContactYourAdminToGetAccessToThisApplication";
	}

	return (
		<>
			<ContentBlock size="small">
				<h1>{localize(titleKey)}</h1>

				{descriptionKey && (
					<h2>{localize(descriptionKey)}</h2>
				)}
			</ContentBlock>

			{integrationCredentialsDialog}
		</>
	);
};
GoPage.displayName = "GoPage";

export function init(model: AppLinkNavigationModel): void {
	createRoot(
		ensureNotNil(
			document.getElementById("app-link-go-page")
		)
	)
		.render(
			<GoPage model={model} />
		);
}
